import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Calendar, Download, MapPin, MessageCircle, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { useApi, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { pushRecent, track } from "../lib/store";
import { fmtDual, fmtNum, waLink } from "../lib/format";
import { DemoBadge, Loading, Reveal } from "../components/Common";
import Gallery, { Lightbox } from "../components/Gallery";
import PropertyCard, { FavButton, PriceTag } from "../components/PropertyCard";
import MortgageCalculator from "../components/MortgageCalculator";
import LeadForm from "../components/LeadForm";
import ViewingForm from "../components/ViewingForm";
import ShareButtons from "../components/ShareButtons";
import { PropertiesMap } from "../components/Maps";
import RecentlyViewed from "../components/RecentlyViewed";
import Seo from "../components/Seo";
import { Unavailable } from "./NotFound";
import PanoramaViewer from "../components/PanoramaViewer";
import InvestmentCalculator from "../components/InvestmentCalculator";
import { Recommended } from "../components/Recommendations";
import { BACKEND } from "../lib/api";

const Block = ({ title, children, testid }) => (
  <Reveal as="section" className="border-t border-fg/10 py-10" >
    <h2 data-testid={testid} className="h-display mb-6 text-2xl md:text-3xl">{title}</h2>
    {children}
  </Reveal>
);

function SpecsTable({ p }) {
  const { t, lang } = useI18n();
  const { label, cityName, nbName } = useSite();
  const rows = [
    [t("property.ref"), p.ref], [t("property.type"), label("property_type", p.type)], [t("property.purpose"), t(`purpose.${p.purpose}`)],
    [t("property.size"), `${fmtNum(p.size_sqm, lang)} ${t("listing.sqm")}`],
    p.bedrooms > 0 && [t("listing.bedrooms"), fmtNum(p.bedrooms, lang)], p.bathrooms > 0 && [t("listing.bathrooms"), fmtNum(p.bathrooms, lang)],
    [t("property.parking"), fmtNum(p.parking, lang)], [t("property.furnishing"), t(`listing.${p.furnishing}`)],
    [t("listing.completion"), t(`listing.${p.completion_status}`)], p.completion_date && [t("property.completionDate"), fmtDual(p.completion_date, lang)],
    [t("property.status"), t(`listing.${p.availability}`)], [t("property.location"), `${nbName(p.city, p.neighborhood)}, ${cityName(p.city)}`],
  ].filter(Boolean);
  return (
    <table data-testid="property-specs-table" className="w-full text-sm">
      <tbody>{rows.map(([k, v]) => (<tr key={k} className="border-b border-fg/5"><th scope="row" className="py-3 text-start font-normal text-fg/50">{k}</th><td className="py-3 text-end text-fg">{v}</td></tr>))}</tbody>
    </table>
  );
}

function Tour({ p }) {
  const { t } = useI18n();
  const yt = p.video_url?.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return (
    <>
      {p.video_url && (
        <Block title={t("property.video")} testid="property-video-heading">
          {yt ? <iframe title={t("property.video")} src={`https://www.youtube-nocookie.com/embed/${yt[1]}`} className="aspect-video w-full rounded-sm" allowFullScreen />
            : <video controls preload="metadata" className="aspect-video w-full rounded-sm bg-overlay"><source src={p.video_url} /></video>}
        </Block>
      )}
      {p.panorama_url && (
        <Block title={t("property.tour")} testid="property-tour-heading">
          <PanoramaViewer url={p.panorama_url} title={t("property.tour")} />
        </Block>
      )}
      {p.tour_embed_url && (
        <Block title={t("property.tour")} testid="property-tour-embed-heading">
          <iframe title={t("property.tour")} src={p.tour_embed_url} data-testid="property-tour-embed" className="aspect-video w-full rounded-sm" allow="xr-spatial-tracking; fullscreen" allowFullScreen />
        </Block>
      )}
    </>
  );
}

function Sidebar({ p, agent, settings }) {
  const { t, tr, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const wa = waLink(settings?.whatsapp || agent?.whatsapp, `${t("property.waMsg")} "${tr(p, "title")}" (${t("property.ref")}: ${p.ref}) ${window.location.href}`);
  return (
    <aside className="space-y-6 lg:sticky lg:top-28">
      <div className="glass rounded-sm p-6">
        <PriceTag p={p} className="block font-display text-4xl text-fg" />
        {tr(p, "payment_plan") && <p className="mt-3 text-xs text-fg/55">{t("property.paymentPlan")}: <span data-testid="property-payment-plan">{tr(p, "payment_plan")}</span></p>}
        <div className="mt-6 grid gap-3">
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="property-whatsapp-button" className="btn-brand w-full"><MessageCircle className="h-4 w-4" />{t("property.whatsapp")}</a>
          <button type="button" data-testid="property-schedule-button" onClick={() => setOpen(true)} className="btn-cta w-full"><Calendar className="h-4 w-4" />{t("property.schedule")}</button>
          <a href={p.brochure_url ? mediaUrl(p.brochure_url) : `${BACKEND}/api/brochure/property/${p.slug}.pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="property-brochure-button" className="btn-ghost w-full"><Download className="h-4 w-4" />{t("property.brochure")}</a>
        </div>
      </div>
      {agent && (
        <div data-testid="property-agent-card" className="glass flex items-center gap-4 rounded-sm p-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand font-display text-xl text-cta">{tr(agent, "name").charAt(0)}</div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-fg/45">{t("property.agent")}</p>
            <p className="truncate font-medium text-fg">{tr(agent, "name")}</p>
            <p className="truncate text-xs text-fg/55">{tr(agent, "title")}</p>
          </div>
          {agent.is_demo && <DemoBadge testid="agent-demo-badge" />}
          <a href={`mailto:${agent.email}`} data-testid="agent-email-link" aria-label={agent.email} className="rounded-full border border-fg/15 p-2.5 text-cta hover:border-cta"><Phone className="h-4 w-4" /></a>
        </div>
      )}
      <div className="glass rounded-sm p-6">
        <h2 className="h-display mb-5 text-xl">{t("property.inquiry")}</h2>
        <LeadForm type="inquiry" propertyId={p.id} prefix="inquiry" defaultMessage={`${t("property.waMsg")} ${tr(p, "title")} (${p.ref})`} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="schedule-viewing-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-cta/20 bg-surface text-fg">
          <DialogTitle className="h-display text-2xl">{t("property.schedule")}</DialogTitle>
          <DialogDescription className="text-fg/60">{tr(p, "title")} · {p.ref}</DialogDescription>
          <ViewingForm propertyId={p.id} prefix="property-viewing" onDone={() => setTimeout(() => setOpen(false), 1200)} />
        </DialogContent>
      </Dialog>
    </aside>
  );
}

export default function PropertyDetail() {
  const { slug } = useParams();
  const { t, tr, lang } = useI18n();
  const { label, cityName, nbName, settings } = useSite();
  const { data, loading, error } = useApi(`/properties/${slug}`);
  const [plan, setPlan] = useState(null);
  const p = data?.property;
  useEffect(() => { if (p) { pushRecent(p.id); track("property_view", { property_id: p.id }); } }, [p]);
  if (loading) return <Loading />;
  if (error || !p) return <Unavailable kind="property" />;
  const title = tr(p, "title");
  const jsonLd = {
    "@context": "https://schema.org", "@type": "RealEstateListing", name: title, description: tr(p, "description"), url: window.location.href,
    image: p.images, datePosted: p.created_at, inLanguage: lang,
    offers: { "@type": "Offer", price: p.price, priceCurrency: "SAR", availability: p.availability === "available" ? "https://schema.org/InStock" : "https://schema.org/LimitedAvailability" },
    address: { "@type": "PostalAddress", addressLocality: cityName(p.city), addressCountry: "SA" }, geo: { "@type": "GeoCoordinates", latitude: p.lat, longitude: p.lng },
  };
  return (
    <article data-testid="property-detail-page" className="pt-28">
      <Seo seo={p.seo} title={title} description={tr(p, "description")} image={mediaUrl(p.images[0])} type="article" jsonLd={jsonLd} />
      <div className="container-lux">
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-fg/50"><Link to="/properties" className="hover:text-cta">{t("nav.properties")}</Link> / <span>{p.ref}</span></nav>
        <div className="mb-8 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {p.is_demo && <DemoBadge label={t("property.demo")} testid="property-demo-badge" />}
              <span className="chip !py-1">{t(`purpose.${p.purpose}`)}</span>
              <span className="chip !py-1">{label("property_type", p.type)}</span>
              <span className="chip !py-1">{t(`listing.${p.completion_status}`)}</span>
              <span className="chip !py-1">{t(`listing.${p.availability}`)}</span>
            </div>
            <h1 data-testid="property-title" className="h-display text-4xl sm:text-5xl">{title}</h1>
            <p className="mt-3 flex items-center gap-2 text-fg/60"><MapPin className="h-4 w-4 text-cta" />{nbName(p.city, p.neighborhood)}, {cityName(p.city)} · {t("property.ref")} {p.ref}</p>
            {data.project && <p className="mt-2 text-sm text-fg/60">{t("property.partOf")}: <Link to={`/developments/${data.project.slug}`} data-testid="property-project-link" className="text-cta hover:underline">{tr(data.project, "title")}</Link></p>}
          </div>
          <div className="flex items-center gap-3"><FavButton id={p.id} /><ShareButtons title={title} /></div>
        </div>
        <Gallery images={p.images} title={title} />
      </div>

      <div className="container-lux grid gap-12 py-12 lg:grid-cols-[1fr_380px]">
        <div>
          <Block title={t("property.overview")} testid="property-overview-heading"><p data-testid="property-description" className="text-base leading-relaxed text-fg/70">{tr(p, "description")}</p></Block>
          <Block title={t("property.specs")} testid="property-specs-heading"><SpecsTable p={p} /></Block>
          {p.smart_features?.length > 0 && (
            <Block title={lang === "ar" ? "مزايا المنزل الذكي" : "Smart-home features"} testid="property-smart-features-heading">
              <ul data-testid="property-smart-features" className="grid grid-cols-2 gap-3 sm:grid-cols-3">{p.smart_features.map((a) => (<li key={a} className="flex items-center gap-2 text-sm text-fg/75"><BadgeCheck className="h-4 w-4 text-cta" />{label("smart_feature", a)}</li>))}</ul>
            </Block>
          )}
          {p.amenities.length > 0 && (
            <Block title={t("property.amenities")} testid="property-amenities-heading">
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">{p.amenities.map((a) => (<li key={a} className="flex items-center gap-2 text-sm text-fg/75"><BadgeCheck className="h-4 w-4 text-cta" />{label("amenity", a)}</li>))}</ul>
            </Block>
          )}
          {p.floor_plans.length > 0 && (
            <Block title={t("property.floorPlans")} testid="property-floorplans-heading">
              <div className="grid gap-3 sm:grid-cols-2">{p.floor_plans.map((f, i) => (<button key={f} type="button" data-testid={`floorplan-${i}`} onClick={() => setPlan(i)} className="overflow-hidden rounded-sm border border-fg/10"><img src={mediaUrl(f)} alt={`${t("property.floorPlans")} ${i + 1}`} className="aspect-[4/3] w-full object-cover" /></button>))}</div>
              <Lightbox images={p.floor_plans} index={plan} onClose={() => setPlan(null)} title={t("property.floorPlans")} />
            </Block>
          )}
          <Tour p={p} />
          <Block title={t("property.location")} testid="property-location-heading">
            <PropertiesMap items={[p]} single className="h-[360px]" />
            {p.nearby.length > 0 && (
              <ul data-testid="property-nearby-list" className="mt-6 grid gap-3 sm:grid-cols-2">
                {p.nearby.map((n) => (<li key={n.name_en} className="flex justify-between border-b border-fg/5 py-2 text-sm"><span className="text-fg/75">{tr(n, "name")}</span><span className="text-cta">{fmtNum(n.distance_km, lang)} {t("property.km")}</span></li>))}
              </ul>
            )}
          </Block>
          {p.purpose !== "rent" && <Reveal className="border-t border-fg/10 py-10"><MortgageCalculator price={p.price} /></Reveal>}
          {p.purpose !== "rent" && p.category !== "land" && <Reveal className="border-t border-fg/10 py-10"><InvestmentCalculator price={p.price} rent={Math.round((p.price * 0.06) / 12 / 100) * 100} /></Reveal>}
        </div>
        <Sidebar p={p} agent={data.agent} settings={settings} />
      </div>

      {data.similar.length > 0 && (
        <section className="container-lux border-t border-fg/10 py-16">
          <h2 className="h-display mb-8 text-3xl">{t("property.similar")}</h2>
          <div data-testid="similar-properties" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{data.similar.map((s) => (<PropertyCard key={s.id} p={s} />))}</div>
        </section>
      )}
      <Recommended excludeId={p.id} />
      <RecentlyViewed excludeId={p.id} />
    </article>
  );
}
